import { memo } from "react";
import { Trans } from "react-i18next";
import { Badge, Text } from "react-native-paper";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import Box from "../../Box";
import Button from "../../Button";
import TooltipWrapper from "../../TooltipWrapper";

/* -----------------Types--------------- */
type NextButtonState = "skip" | "next" | "finish";

const getNextButtonText = (
	nextButtonState: NextButtonState,
	texts: ButtonConfig["texts"] = {},
) => {
	switch (nextButtonState) {
		case "skip":
			return texts.skip;
		case "finish":
			return texts.finish;
		case "next":
			return texts.next;
		default:
			return texts.next;
	}
};

const getNextButtonMode = (
	nextButtonState: NextButtonState,
	disabledNext?: boolean,
) => {
	return nextButtonState === "skip" || disabledNext ? "outlined" : "contained";
};

const getNextButtonBackgroundColor = (
	nextButtonState: NextButtonState,
	theme: ReturnType<typeof useAppTheme>,
) => {
	return nextButtonState === "skip" ? "transparent" : theme.colors.primary;
};

const getNextButtonTextColor = (
	nextButtonState: NextButtonState,
	isSummativeTest: boolean,
	theme: ReturnType<typeof useAppTheme>,
) => {
	return isSummativeTest && nextButtonState === "skip"
		? theme.colors.onSurface
		: undefined;
};

export type QuestionsConfig = {
	onPress: () => void;
	activeFilterCount?: number;
	label: string;
	a11yLabel?: string;
	tooltip?: string;
};

export type ButtonConfig = {
	texts?: {
		next?: string;
		previous?: string;
		skip?: string;
		finish?: string;
		help?: string;
	};
	disabled?: {
		next?: boolean;
		previous?: boolean;
	};
	tooltip?: {
		next?: string;
		previous?: string;
	};
	icons?: {
		next?: string;
		previous?: string;
	};
};

export type AboutConfig = {
	onPress: () => void;
	label: string;
};

export type ExitConfig = {
	onPress: () => void;
	label: string;
};

export type NextButtonProps = {
	handlePrevious: () => void;
	handleNext: () => void;
	showPreviousButton?: boolean;
	nextButtonTooltip?: string;
	nextButtonState?: NextButtonState;
	buttonConfig?: ButtonConfig;
	isSummativeTest?: boolean;
	toggleHelpDialog?: () => void;
	inReviewMode?: boolean;
	questionsConfig?: QuestionsConfig;
	progressIndicator?: { current: number | string; total: number | string };
	aboutConfig?: AboutConfig;
	exitConfig?: ExitConfig;
};

/* -----------------PreviousButton--------------- */
type PreviousButtonProps = {
	showPreviousButton: boolean;
	handlePrevious: () => void;
	previousButtonIcon: string;
	previousButtonText?: string;
	disabled?: boolean;
	tooltip?: string;
	isSummativeTest: boolean;
};

const PreviousButton = memo(
	({
		showPreviousButton,
		handlePrevious,
		previousButtonIcon,
		previousButtonText,
		disabled,
		tooltip,
		isSummativeTest,
	}: PreviousButtonProps) => {
		const theme = useAppTheme();
		if (!showPreviousButton) {
			return <Box width={96} />;
		}
		const button = (
			<Button
				mode="outlined"
				icon={previousButtonIcon}
				accessible
				accessibilityLabel="Previous question"
				style={{
					borderColor: theme.palette.divider,
				}}
				textColor={isSummativeTest ? theme.colors.onSurface : undefined}
				onPress={handlePrevious}
				disabled={disabled}
			>
				{previousButtonText}
			</Button>
		);
		if (tooltip) {
			return <TooltipWrapper title={tooltip}>{button}</TooltipWrapper>;
		}
		return button;
	},
);
PreviousButton.displayName = "PreviousButton";

/* -----------------NextButton--------------- */
type NextButtonRenderProps = {
	handleNext: () => void;
	nextButtonState: NextButtonState;
	nextButtonMode: "outlined" | "contained";
	nextButtonIcon: string;
	nextButtonText?: string;
	nextButtonTextColor?: string;
	disabled?: boolean;
	tooltip?: string;
};

const NextButton = memo(
	({
		handleNext,
		nextButtonState,
		nextButtonMode,
		nextButtonIcon,
		nextButtonText,
		nextButtonTextColor,
		disabled,
		tooltip,
	}: NextButtonRenderProps) => {
		const theme = useAppTheme();
		const bg = getNextButtonBackgroundColor(nextButtonState, theme);

		const button = (
			<Button
				onPress={handleNext}
				mode={nextButtonMode}
				style={{
					borderColor: theme.palette.divider,
					backgroundColor: disabled ? theme.colors.surface : bg,
				}}
				textColor={nextButtonTextColor}
				asset="endIcon"
				icon={nextButtonIcon}
				contentStyle={{
					flexDirection: "row-reverse",
				}}
				disabled={disabled}
				accessibilityLabel={`${nextButtonText} question`}
				accessible
			>
				{nextButtonText}
			</Button>
		);
		if (tooltip) {
			return <TooltipWrapper title={tooltip}>{button}</TooltipWrapper>;
		}
		return button;
	},
);
NextButton.displayName = "NextButton";

/* -----------------SessionGoToQuestionButton--------------- */
const SessionGoToQuestionButton = ({
	config,
	isSummativeTest,
}: {
	config: QuestionsConfig;
	isSummativeTest?: boolean;
}) => {
	const { onPress, activeFilterCount = 0, label, a11yLabel, tooltip } = config;
	const hasActiveFilters = activeFilterCount > 0;
	const accessibilityLabel = a11yLabel ? `${label}, ${a11yLabel}` : label;
	const theme = useAppTheme();

	const button = (
		<Box position="relative" alignSelf="flex-start">
			<Button
				mode="outlined"
				icon="view-list-outline"
				onPress={onPress}
				accessibilityLabel={accessibilityLabel}
				{...(tooltip ? { accessibilityHint: tooltip } : {})}
				textColor={isSummativeTest ? theme.colors.onSurface : undefined}
				style={{
					borderColor: theme.palette.divider,
				}}
			>
				{label}
			</Button>
			{hasActiveFilters && (
				<Box
					pointerEvents="none"
					position="absolute"
					top={-4}
					right={-4}
					zIndex={1}
					alignItems="center"
					justifyContent="center"
				>
					<Badge
						size={activeFilterCount > 9 ? 20 : 18}
						style={{ backgroundColor: theme.palette.error.main }}
					>
						{activeFilterCount > 9 ? "9+" : String(activeFilterCount)}
					</Badge>
				</Box>
			)}
		</Box>
	);

	if (tooltip) {
		return <TooltipWrapper title={tooltip}>{button}</TooltipWrapper>;
	}
	return button;
};

/* -----------------ExitButton--------------- */
const ExitButton = ({ onPress, label }: ExitConfig) => {
	const theme = useAppTheme();

	return (
		<Button
			mode="outlined"
			icon="logout"
			onPress={onPress}
			textColor={theme.colors.onSurface}
			style={{
				borderColor: theme.palette.divider,
			}}
			accessibilityLabel={label}
		>
			{label}
		</Button>
	);
};

/* -----------------AboutButton--------------- */
const AboutButton = ({ onPress, label }: AboutConfig) => {
	const theme = useAppTheme();

	return (
		<Button
			icon="information-outline"
			onPress={onPress}
			textColor={theme.colors.onSurface}
			accessibilityLabel={label}
		>
			{label}
		</Button>
	);
};

/* -----------------Component--------------- */
const NavButtons = ({
	handlePrevious,
	handleNext,
	showPreviousButton = false,
	nextButtonState = "next",
	buttonConfig = {},
	isSummativeTest = false,
	inReviewMode = false,
	toggleHelpDialog,
	nextButtonTooltip,
	questionsConfig,
	progressIndicator,
	aboutConfig,
	exitConfig,
}: NextButtonProps) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const aboutButton = aboutConfig ? <AboutButton {...aboutConfig} /> : null;
	const exitButton = exitConfig ? <ExitButton {...exitConfig} /> : null;

	const { texts = {}, disabled = {}, tooltip = {}, icons = {} } = buttonConfig;

	const resolvedTooltip = {
		next: tooltip.next ?? nextButtonTooltip,
	};

	const nextButtonMode = getNextButtonMode(nextButtonState, disabled.next);

	const defaultNextIcon =
		nextButtonState === "skip" ? "skip-next-outline" : "chevron-right";
	const nextButtonIcon = icons.next ?? defaultNextIcon;

	const previousButtonIcon = icons.previous ?? "chevron-left";
	const nextButtonText = getNextButtonText(nextButtonState, texts);
	const nextButtonTextColor = getNextButtonTextColor(
		nextButtonState,
		isSummativeTest,
		theme,
	);

	const previousButton = (
		<PreviousButton
			showPreviousButton={showPreviousButton}
			handlePrevious={handlePrevious}
			previousButtonIcon={previousButtonIcon}
			previousButtonText={texts.previous}
			disabled={disabled.previous}
			tooltip={tooltip.previous}
			isSummativeTest={isSummativeTest}
		/>
	);

	const nextButton = (
		<NextButton
			handleNext={handleNext}
			nextButtonState={nextButtonState}
			nextButtonMode={nextButtonMode}
			nextButtonIcon={nextButtonIcon}
			nextButtonText={nextButtonText}
			nextButtonTextColor={nextButtonTextColor}
			disabled={disabled.next}
			tooltip={resolvedTooltip.next}
		/>
	);

	if (isMobile) {
		return (
			<Box
				flexDirection="row"
				justifyContent="space-between"
				alignItems="center"
				width="100%"
				gap={8}
			>
				{exitButton}
				{previousButton}
				{progressIndicator ? (
					<Text
						style={{
							color: theme.colors.onSurface,
							fontSize: 14,
						}}
					>
						<Trans
							i18nKey="testDriver.progressBar.questionProgress"
							values={{
								current: progressIndicator.current,
								total: progressIndicator.total,
							}}
							components={{
								b: <Text style={{ fontWeight: "700" }}>{""}</Text>,
							}}
						/>
					</Text>
				) : null}
				{nextButton}
			</Box>
		);
	}

	const hasLeftCluster =
		isSummativeTest || Boolean(aboutConfig) || Boolean(exitConfig);

	const navButtons = (
		<Box flexDirection="row" gap={16}>
			{previousButton}
			{nextButton}
		</Box>
	);

	return (
		<Box
			flexDirection="row"
			justifyContent={hasLeftCluster ? "space-between" : "flex-end"}
			alignItems="center"
			width="100%"
			gap={8}
		>
			{hasLeftCluster && (
				<Box flexDirection="row" alignItems="center" gap={8}>
					{exitButton}
					{isSummativeTest && questionsConfig && !isMobile && (
						<SessionGoToQuestionButton
							config={questionsConfig}
							isSummativeTest={isSummativeTest}
						/>
					)}
					{aboutButton}
					{isSummativeTest &&
						!(inReviewMode || isMobile) &&
						toggleHelpDialog && (
							<Button
								icon={"help-circle-outline"}
								textColor={theme.colors.onSurface}
								onPress={toggleHelpDialog}
							>
								{texts.help}
							</Button>
						)}
				</Box>
			)}
			{navButtons}
		</Box>
	);
};

export default NavButtons;
